/* eslint-disable no-nested-ternary */
import { QuestionOption } from '@app/utils/hooks';
import {
  AnswerType,
  CriteriaType,
  QuestionCriterionView,
  QuestionType,
  TrialCriteriaView,
} from '@data/sdk/match';
import { AntdForm } from '@ui/components';

export enum ModifyMode {
  CreateNew = 'create-new',
  ModifyExisting = 'modify-existing',
}

export type EditCriteriaFormValues = {
  answerType: AnswerType;
  autoTranslate?: boolean;
  maxThreshold?: number | null;
  minThreshold?: number | null;
  mode: ModifyMode;
  originalCriteriaText: string;
  otherValues?: string[] | null;
  question: Partial<QuestionOption>;
  questionText?: string;
  type: CriteriaType;
  unit?: string | null;
  value?: boolean | null;
};

export const useFormData = (
  criteria:
    | TrialCriteriaView
    | (QuestionCriterionView & {
        answerType: AnswerType;
        questionId: string;
        questionText: string;
      }),
) => {
  const [form] = AntdForm.useForm<EditCriteriaFormValues>();

  const question = AntdForm.useWatch('question', form);
  const answerType = AntdForm.useWatch('answerType', form);

  const mode = AntdForm.useWatch('mode', form);
  const autoTranslate = AntdForm.useWatch('autoTranslate', form);
  const questionText = AntdForm.useWatch('questionText', form);

  // Standard questions are shared across all trials; editing their text mutates
  // them globally. Steer criteria edits to a new trial-specific question instead.
  const isSharedStandardQuestion =
    (criteria as TrialCriteriaView).questionType === QuestionType.Standard;

  const initialValues: EditCriteriaFormValues = {
    type: criteria.type,
    originalCriteriaText:
      (criteria as TrialCriteriaView)?.originalCriteriaText ||
      (criteria as QuestionCriterionView)?.criterionText,
    question: { value: criteria.questionId, label: criteria.questionText },
    questionText: criteria.questionText,
    mode: isSharedStandardQuestion ? ModifyMode.CreateNew : ModifyMode.ModifyExisting,
    answerType:
      criteria.answerType === AnswerType.Boolean || criteria.answerType === AnswerType.Number
        ? criteria.answerType
        : AnswerType.Boolean,
    value: criteria.answerType === AnswerType.Boolean ? criteria.value : null,
    minThreshold: criteria.minThreshold !== null ? criteria.minThreshold : null,
    maxThreshold: criteria.maxThreshold !== null ? criteria.maxThreshold : null,
    unit: criteria.unit ?? undefined,
    otherValues: criteria.otherValues,
    autoTranslate: true,
  };

  const isICDCodeCriteria = !!(initialValues.otherValues && initialValues.otherValues.length > 0);

  return {
    form,
    question,
    questionText,
    answerType,
    mode,
    autoTranslate,
    initialValues,
    isICDCodeCriteria,
    isSharedStandardQuestion,
  };
};
